// 전자결재 — 결재함(목록) · 문서 상세(양식 렌더 + 결재선 + 승인/반려/회수) · 작성(동적 양식 + 결재선/참조 지정)
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../shared/api/client";
import { useAuth } from "../../shared/stores/auth";
import { toast } from "../../shared/stores/toasts";
import { I } from "../../shared/Icon";
import { OrgPicker } from "../../shared/org/OrgPicker";

// ── Types ──────────────────────────────────────────────────────────────────
type Field = { name: string; label: string; type: string; required?: boolean; options?: string[] };
type FormT = { id: number; code: string; name: string; category: string; description?: string | null; template_json: { fields?: Field[] }; default_line?: any };
type Line = { id?: number; step_order: number; approver_id: number; approver_name?: string | null; type: string; decision: string; decision_comment?: string | null; decided_at?: string | null; delegated_to?: number | null; delegated_to_name?: string | null };
type Ref = { user_id: number | null; name?: string | null; ref_type: string; read_at?: string | null };
type Doc = {
  id: number; doc_no: string; form_code: string; form_name?: string | null; title: string;
  body_json: Record<string, any>; status: string; current_step: number;
  author_id: number; author_name?: string | null; submitted_at?: string | null; completed_at?: string | null;
  created_at: string; lines: Line[]; references: Ref[]; attachments?: Attach[];
};
type UserLite = { id: number; name: string; email?: string };
type PickUser = UserLite & { parallel?: boolean };
type Attach = { id: number; file_name: string; file_size: number; mime_type?: string | null; uploaded_by_name?: string | null };
type Cmt = { id: number; author_id: number; author_name?: string | null; body: string; created_at: string; att_name?: string | null; att_size?: number | null; att_mime?: string | null };

const TAB_LABEL: Record<string, string> = { waiting: "결재 대기", drafts: "임시 저장", in_progress: "진행 중", completed: "완료", rejected: "반려", referenced: "수신·참조" };
const CAT_LABEL: Record<string, string> = { HR: "인사", FINANCE: "재무", GENERAL: "일반", CONTRACT: "계약", ATTENDANCE: "근태", SALES: "영업" };
const STATUS: Record<string, { label: string; bg: string; fg: string }> = {
  DRAFT: { label: "임시", bg: "var(--panel)", fg: "var(--text-3)" },
  PENDING: { label: "진행 중", bg: "#fef3c7", fg: "#92400e" },
  IN_PROGRESS: { label: "진행 중", bg: "#fef3c7", fg: "#92400e" },
  APPROVED: { label: "완료", bg: "#dcfce7", fg: "#166534" },
  REJECTED: { label: "반려", bg: "#fee2e2", fg: "#991b1b" },
  CANCELED: { label: "취소", bg: "var(--panel)", fg: "var(--text-3)" },
};
const DECISION: Record<string, { label: string; bg: string; fg: string }> = {
  PENDING: { label: "대기", bg: "var(--panel)", fg: "var(--text-3)" },
  APPROVED: { label: "승인", bg: "#dcfce7", fg: "#166534" },
  REJECTED: { label: "반려", bg: "#fee2e2", fg: "#991b1b" },
  SKIPPED: { label: "생략", bg: "var(--panel)", fg: "var(--text-3)" },
};
function fmtDate(s?: string | null) { return s ? new Date(s).toLocaleString("ko-KR", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""; }
function escapeHtml(s: string) { return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!)); }
function Badge({ m }: { m: { label: string; bg: string; fg: string } }) {
  return <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: m.bg, color: m.fg }}>{m.label}</span>;
}

// ── User search picker (결재선 / 참조) ───────────────────────────────────────
function UserPicker({ label, value, onChange, ordered, parallelToggle, excludeId }: {
  label: string; value: PickUser[]; onChange: (v: PickUser[]) => void; ordered?: boolean; parallelToggle?: boolean; excludeId?: number | null;
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<UserLite[]>([]);
  const [open, setOpen] = useState(false);
  const [orgOpen, setOrgOpen] = useState(false);
  const addMany = (us: UserLite[]) => onChange([...value, ...us.filter((u) => u.id !== excludeId && !value.some((x) => x.id === u.id))]);
  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const r = await api.get<{ data: UserLite[] }>(`/users?q=${encodeURIComponent(q)}&size=8`); setResults(r.data?.data || []); setOpen(true); }
      catch { setResults([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);
  const add = (u: UserLite) => { if (u.id !== excludeId && !value.some((x) => x.id === u.id)) onChange([...value, u]); setQ(""); setResults([]); setOpen(false); };
  const remove = (id: number) => onChange(value.filter((x) => x.id !== id));
  const move = (i: number, d: number) => { const j = i + d; if (j < 0 || j >= value.length) return; const arr = [...value]; [arr[i], arr[j]] = [arr[j], arr[i]]; onChange(arr); };
  const toggleP = (i: number) => onChange(value.map((u, idx) => idx === i ? { ...u, parallel: !u.parallel } : u));

  return (
    <div>
      <label className="t-label">{label}</label>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {value.map((u, i) => (
          <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 10px", border: "1px solid var(--border)", borderRadius: 8, background: "var(--panel)" }}>
            {ordered && <span style={{ fontSize: 11, fontWeight: 700, color: "var(--p-600,#4f46e5)", width: 18 }}>{u.parallel && i > 0 ? "∥" : i + 1}</span>}
            <span style={{ flex: 1, fontSize: 13 }}>{u.name}{u.email ? <span style={{ color: "var(--text-3)", fontSize: 11 }}> · {u.email}</span> : null}</span>
            {parallelToggle && i > 0 && (
              <button className="t-btn" style={{ fontSize: 10.5, padding: "1px 6px", color: u.parallel ? "var(--p-600,#4f46e5)" : "var(--text-3)" }}
                title="이전 결재자와 동시(병렬) 결재" onClick={() => toggleP(i)}>{u.parallel ? "동시" : "순차"}</button>
            )}
            {ordered && <>
              <button className="t-btn" style={{ fontSize: 11, padding: "1px 6px" }} onClick={() => move(i, -1)} disabled={i === 0}>▲</button>
              <button className="t-btn" style={{ fontSize: 11, padding: "1px 6px" }} onClick={() => move(i, 1)} disabled={i === value.length - 1}>▼</button>
            </>}
            <button className="t-btn" style={{ fontSize: 11, padding: "1px 6px", color: "#ef4444" }} onClick={() => remove(u.id)}>✕</button>
          </div>
        ))}
        <div style={{ display: "flex", gap: 6 }}>
          <div style={{ position: "relative", flex: 1 }}>
            <input className="t-input" style={{ width: "100%", fontSize: 13 }} placeholder="이름으로 검색…" value={q}
              onChange={(e) => setQ(e.target.value)} onFocus={() => results.length && setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} />
            {open && results.length > 0 && (
              <div style={{ position: "absolute", top: 36, left: 0, right: 0, zIndex: 30, background: "var(--surface,#fff)", border: "1px solid var(--border)", borderRadius: 8, boxShadow: "var(--shadow-md,0 8px 24px rgba(0,0,0,.15))", maxHeight: 220, overflowY: "auto" }}>
                {results.filter((u) => u.id !== excludeId).map((u) => (
                  <div key={u.id} onMouseDown={(e) => { e.preventDefault(); add(u); }} style={{ padding: "8px 12px", fontSize: 13, cursor: "pointer" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--hover)")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                    {u.name}{u.email ? <span style={{ color: "var(--text-3)", fontSize: 11 }}> · {u.email}</span> : null}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button className="t-btn" style={{ fontSize: 12, whiteSpace: "nowrap" }} onClick={() => setOrgOpen(true)}>🏢 조직도</button>
        </div>
      </div>
      {orgOpen && <OrgPicker onPick={(us) => addMany(us.map((u) => ({ id: u.id, name: u.name, email: u.email ?? undefined })))} onClose={() => setOrgOpen(false)} excludeIds={excludeId != null ? [excludeId] : []} />}
    </div>
  );
}

// ── Field input (작성) ───────────────────────────────────────────────────────
function FieldInput({ f, value, onChange }: { f: Field; value: any; onChange: (v: any) => void }) {
  const common: any = { className: "t-input", style: { width: "100%", fontSize: 13 }, value: value ?? "", onChange: (e: any) => onChange(e.target.value) };
  return (
    <div>
      <label className="t-label">{f.label}{f.required && <span style={{ color: "#ef4444" }}> *</span>}</label>
      {f.type === "textarea" ? <textarea {...common} rows={3} style={{ ...common.style, resize: "vertical" }} />
        : f.type === "select" ? (
          <select {...common}><option value="">선택…</option>{(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}</select>
        ) : f.type === "attachment" ? <div style={{ fontSize: 12, color: "var(--text-3)", padding: "6px 0" }}>첨부 파일 기능은 곧 제공됩니다</div>
        : <input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} {...common} />}
    </div>
  );
}

// ── Compose ─────────────────────────────────────────────────────────────────
function Compose({ composeParam, onDone, onCancel }: { composeParam: string; onDone: (tab: string) => void; onCancel: () => void }) {
  const me = useAuth((s) => s.user?.id ?? null);
  const [forms, setForms] = useState<FormT[]>([]);
  const [form, setForm] = useState<FormT | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState<Record<string, any>>({});
  const [line, setLine] = useState<PickUser[]>([]);
  const [refs, setRefs] = useState<PickUser[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (composeParam === "1") {
      api.get<FormT[]>("/approval/forms").then((r) => setForms(r.data)).catch(() => {});
    } else {
      api.get<FormT>(`/approval/forms/${composeParam}`).then((r) => { setForm(r.data); setTitle(r.data.name); }).catch(() => toast.error("양식을 불러오지 못했습니다"));
    }
  }, [composeParam]);

  if (!form) {
    const byCat = forms.reduce<Record<string, FormT[]>>((a, f) => { (a[f.category] ||= []).push(f); return a; }, {});
    return (
      <div style={{ height: "100%", overflowY: "auto", padding: "24px 28px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <button className="t-btn" onClick={onCancel} style={{ fontSize: 12 }}>← 결재함</button>
          <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800 }}>결재 작성 — 양식 선택</h2>
        </div>
        <p style={{ margin: "0 0 18px", fontSize: 13, color: "var(--text-3)" }}>사용할 결재 양식을 선택하세요.</p>
        {Object.keys(byCat).sort().map((cat) => (
          <div key={cat} style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-3)", marginBottom: 8 }}>{CAT_LABEL[cat] || cat}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
              {byCat[cat].map((f) => (
                <div key={f.code} className="t-card" style={{ padding: 14, cursor: "pointer" }}
                  onClick={() => { setForm(f); setTitle(f.name); }}
                  onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,.1)")}
                  onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>📄 {f.name}</div>
                  {f.description && <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 4 }}>{f.description}</div>}
                </div>
              ))}
            </div>
          </div>
        ))}
        {forms.length === 0 && <div style={{ color: "var(--text-3)", fontSize: 13 }}>양식을 불러오는 중…</div>}
      </div>
    );
  }

  const fields = form.template_json?.fields || [];
  const submit = async (doSubmit: boolean) => {
    if (!title.trim()) { toast.error("제목을 입력하세요"); return; }
    for (const f of fields) if (f.required && f.type !== "attachment" && !String(body[f.name] ?? "").trim()) { toast.error(`'${f.label}' 항목을 입력하세요`); return; }
    // Exclude self + dedupe (기안자는 결재선/참조에 포함 불가)
    const lineUsers = line.filter((u, i, arr) => u.id !== me && arr.findIndex((x) => x.id === u.id) === i);
    const refIds = Array.from(new Set(refs.map((u) => u.id).filter((id) => id !== me)));
    setBusy(true);
    try {
      const r = await api.post<{ id: number }>("/approval/documents", {
        form_code: form.code, title: title.trim(), body,
        approval_line: lineUsers.length ? lineUsers.map((u, i) => ({ approver_id: u.id, type: i > 0 && u.parallel ? "PARALLEL" : "SEQUENTIAL" })) : null,
        references: refIds,
        submit: false,   // create as draft first so we can attach files, then submit
      });
      const docId = r.data.id;
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        try { await api.post(`/approval/documents/${docId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
        catch { toast.error("첨부 실패", f.name); }
      }
      if (doSubmit) await api.post(`/approval/documents/${docId}/submit`);
      toast.success(doSubmit ? "상신되었습니다" : "임시 저장되었습니다");
      window.dispatchEvent(new Event("approval:refresh"));
      onDone(doSubmit ? "in_progress" : "drafts");
    } catch (e: any) {
      toast.error("저장 실패", e?.response?.data?.detail ?? e?.response?.data?.error?.message ?? "");
    } finally { setBusy(false); }
  };

  return (
    <div style={{ height: "100%", overflowY: "auto" }}>
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "24px 20px 56px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <button className="t-btn" onClick={onCancel} style={{ fontSize: 12 }}>← 취소</button>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>{form.name}</h2>
        </div>

        <div className="t-card" style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label className="t-label">제목 <span style={{ color: "#ef4444" }}>*</span></label>
            <input className="t-input" style={{ width: "100%", fontSize: 14 }} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="문서 제목" />
          </div>
          {fields.map((f) => <FieldInput key={f.name} f={f} value={body[f.name]} onChange={(v) => setBody((b) => ({ ...b, [f.name]: v }))} />)}
        </div>

        <div className="t-card" style={{ padding: 18, marginTop: 14, display: "flex", flexDirection: "column", gap: 16 }}>
          <UserPicker label="결재선 (비우면 상급자 자동 지정 · '동시'로 병렬결재)" value={line} onChange={setLine} ordered parallelToggle excludeId={me} />
          <UserPicker label="참조 / 수신" value={refs} onChange={setRefs} excludeId={me} />
          <div>
            <label className="t-label">첨부 파일</label>
            <input type="file" multiple onChange={(e) => { setFiles((p) => [...p, ...Array.from(e.target.files ?? [])]); e.currentTarget.value = ""; }} style={{ fontSize: 12 }} />
            {files.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6 }}>
                {files.map((f, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--text-2)" }}>
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>📎 {f.name}</span>
                    <span style={{ color: "var(--text-3)" }}>{(f.size / 1024).toFixed(0)} KB</span>
                    <button className="t-btn" style={{ fontSize: 11, padding: "1px 6px", color: "#ef4444" }} onClick={() => setFiles((p) => p.filter((_, j) => j !== i))}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button className="t-btn" onClick={() => submit(false)} disabled={busy}>임시 저장</button>
          <button className="t-btn t-btn-primary" onClick={() => submit(true)} disabled={busy}>{busy ? "처리 중…" : "상신"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Approval stamp box (결재 도장) ───────────────────────────────────────────
function Stamp({ decision }: { decision: string }) {
  if (decision === "APPROVED") return <div style={{ width: 44, height: 44, borderRadius: "50%", border: "2px solid #e0392b", color: "#e0392b", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, transform: "rotate(-8deg)" }}>승인</div>;
  if (decision === "REJECTED") return <div style={{ width: 44, height: 44, borderRadius: "50%", border: "2px solid #9aa0a6", color: "#9aa0a6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, transform: "rotate(-8deg)" }}>반려</div>;
  if (decision === "SKIPPED") return <div style={{ fontSize: 12, color: "var(--text-3)", fontWeight: 700 }}>전결</div>;
  return <span style={{ fontSize: 10, color: "var(--text-3)" }}>대기</span>;
}
function StampBox({ doc }: { doc: Doc }) {
  const cols = [
    { label: "기안", name: doc.author_name || `#${doc.author_id}`, decision: "APPROVED", date: doc.submitted_at || doc.created_at },
    ...doc.lines.slice().sort((a, b) => a.step_order - b.step_order).map((l) => ({
      label: l.delegated_to_name ? "대결" : "결재",
      name: l.delegated_to_name || l.approver_name || `#${l.approver_id}`,
      decision: l.decision, date: l.decided_at,
    })),
  ];
  return (
    <div style={{ display: "flex", border: "1px solid #bbb", flexShrink: 0 }}>
      {cols.map((c, i) => (
        <div key={i} style={{ borderLeft: i ? "1px solid #bbb" : "none", minWidth: 66, textAlign: "center", background: "var(--bg-0,#fff)" }}>
          <div style={{ fontSize: 10, padding: "3px 6px", borderBottom: "1px solid #bbb", background: "var(--panel)", color: "var(--text-3)" }}>{c.label}</div>
          <div style={{ height: 52, display: "flex", alignItems: "center", justifyContent: "center" }}><Stamp decision={c.decision} /></div>
          <div style={{ fontSize: 11, fontWeight: 600, padding: "2px 4px", borderTop: "1px solid #eee" }}>{c.name}</div>
          <div style={{ fontSize: 10, color: "var(--text-3)", padding: "0 4px 4px" }}>{c.date ? new Date(c.date).toLocaleDateString("ko-KR") : ""}</div>
        </div>
      ))}
    </div>
  );
}

// ── Detail drawer ────────────────────────────────────────────────────────────
function Detail({ docId, me, onClose, onChanged }: { docId: number; me: number | null; onClose: () => void; onChanged: () => void }) {
  const [doc, setDoc] = useState<Doc | null>(null);
  const [form, setForm] = useState<FormT | null>(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [comments, setComments] = useState<Cmt[] | null>(null);
  const [cbody, setCbody] = useState("");
  const [cfile, setCfile] = useState<File | null>(null);

  const loadComments = useCallback(() => {
    api.get<Cmt[]>(`/approval/documents/${docId}/comments`).then((r) => setComments(r.data)).catch(() => setComments([]));
  }, [docId]);

  useEffect(() => {
    api.get<Doc>(`/approval/documents/${docId}`).then((r) => {
      setDoc(r.data);
      api.get<FormT>(`/approval/forms/${r.data.form_code}`).then((fr) => setForm(fr.data)).catch(() => {});
    }).catch(() => toast.error("문서를 불러오지 못했습니다"));
    loadComments();
  }, [docId, loadComments]);

  const addComment = async () => {
    if (!cbody.trim() && !cfile) return;
    try {
      const fd = new FormData();
      fd.append("body", cbody.trim());
      if (cfile) fd.append("file", cfile);
      await api.post(`/approval/documents/${docId}/comments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCbody(""); setCfile(null); loadComments();
    } catch (e: any) { toast.error("댓글 등록 실패", e?.response?.data?.error?.message ?? e?.response?.data?.detail ?? ""); }
  };
  const delComment = async (id: number) => {
    try { await api.delete(`/approval/comments/${id}`); loadComments(); } catch { toast.error("삭제 실패"); }
  };
  const downloadCommentAtt = async (cid: number, name: string) => {
    try {
      const r = await api.get(`/approval/comments/${cid}/attachment`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data as Blob);
      const el = document.createElement("a"); el.href = url; el.download = name; el.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("다운로드 실패"); }
  };

  const act = async (path: string, payload?: any) => {
    setBusy(true);
    try {
      await api.post(`/approval/documents/${docId}/${path}`, payload);
      toast.success("처리되었습니다");
      onChanged();
    } catch (e: any) {
      toast.error("처리 실패", e?.response?.data?.detail ?? e?.response?.data?.error?.message ?? "");
    } finally { setBusy(false); }
  };

  const downloadAtt = async (a: Attach) => {
    try {
      const r = await api.get(`/approval/attachments/${a.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data as Blob);
      const el = document.createElement("a"); el.href = url; el.download = a.file_name; el.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("다운로드 실패"); }
  };

  const printDoc = () => {
    if (!doc) return;
    const rows = (form?.template_json?.fields?.map((f) => f.name) ?? Object.keys(doc.body_json))
      .map((k) => `<tr><th>${escapeHtml(form?.template_json?.fields?.find((f) => f.name === k)?.label || k)}</th><td>${escapeHtml(String(doc.body_json[k] ?? ""))}</td></tr>`).join("");
    const lines = doc.lines.slice().sort((a, b) => a.step_order - b.step_order)
      .map((l) => `<td style="text-align:center;padding:6px 10px"><div style="font-size:11px;color:#888">${l.step_order}단계</div><div style="font-weight:600">${escapeHtml(l.approver_name || "")}</div><div style="font-size:11px;color:#555">${(DECISION[l.decision] || DECISION.PENDING).label}</div></td>`).join("");
    const w = window.open("", "_blank", "width=820,height=900"); if (!w) return;
    w.document.write(`<!doctype html><meta charset="utf-8"><title>${escapeHtml(doc.title)}</title>
      <style>body{font-family:-apple-system,'Malgun Gothic',sans-serif;margin:32px;color:#1a1a1a}h1{font-size:20px}
      table{border-collapse:collapse;width:100%;margin:14px 0}th,td{border:1px solid #ccc;padding:8px 10px;font-size:13px;text-align:left}
      th{background:#f5f5f5;width:140px}.line{border:1px solid #ccc}.meta{font-size:12px;color:#666;margin-bottom:10px}</style>
      <h1>${escapeHtml(doc.title)}</h1>
      <div class="meta">${escapeHtml(doc.form_name || doc.form_code)} · 문서번호 ${escapeHtml(doc.doc_no)} · 기안 ${escapeHtml(doc.author_name || "")} · ${escapeHtml(fmtDate(doc.created_at))}</div>
      <table class="line"><tr>${lines}</tr></table>
      <table>${rows}</table>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };

  if (!doc) return null;
  const myLine = doc.lines.find((l) => l.step_order === doc.current_step && l.approver_id === me && l.decision === "PENDING");
  const isAuthor = doc.author_id === me;
  const active = doc.status === "PENDING" || doc.status === "IN_PROGRESS";
  const canApprove = !!myLine && active;
  const canRecall = isAuthor && active && doc.lines.every((l) => l.decision === "PENDING");
  const canSubmit = isAuthor && doc.status === "DRAFT";
  const canCancel = isAuthor && (doc.status === "DRAFT" || doc.status === "PENDING");
  const fieldLabel = (k: string) => form?.template_json?.fields?.find((f) => f.name === k)?.label || k;
  const bodyKeys = form?.template_json?.fields?.map((f) => f.name) ?? Object.keys(doc.body_json);

  return (
    <>
      <div className="t-drawer-back" onClick={onClose} />
      <div className="t-detail" style={{ width: 640, maxWidth: "94vw", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <h2 style={{ flex: 1, margin: 0, fontSize: 17, fontWeight: 700 }}>{doc.title}</h2>
            <Badge m={STATUS[doc.status] || STATUS.DRAFT} />
            <button className="t-iconbtn" onClick={printDoc} title="인쇄">🖨️</button>
            <button className="t-iconbtn" onClick={onClose}><I.X size={16} /></button>
          </div>
          <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 6, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <span>{doc.form_name || doc.form_code}</span>
            <span>문서번호 {doc.doc_no}</span>
            <span>기안자 {doc.author_name || `#${doc.author_id}`}</span>
            <span>{fmtDate(doc.created_at)}</span>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          {/* 문서 제목 */}
          <div style={{ textAlign: "center", fontSize: 21, fontWeight: 800, letterSpacing: 6, margin: "2px 0 18px" }}>{doc.form_name || "결 재 문 서"}</div>

          {/* 헤더 정보 + 결재 도장 박스 */}
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap" }}>
            <table style={{ borderCollapse: "collapse", flex: 1, minWidth: 260, fontSize: 12.5 }}><tbody>
              {([["기안자", doc.author_name || `#${doc.author_id}`], ["기안일", new Date(doc.created_at).toLocaleDateString("ko-KR")], ["문서번호", doc.doc_no]] as [string, string][]).map(([k, v]) => (
                <tr key={k}>
                  <th style={{ border: "1px solid var(--border)", background: "var(--panel)", padding: "7px 10px", textAlign: "left", width: 84, fontWeight: 600, color: "var(--text-2)", whiteSpace: "nowrap" }}>{k}</th>
                  <td style={{ border: "1px solid var(--border)", padding: "7px 10px" }}>{v}</td>
                </tr>
              ))}
            </tbody></table>
            {(active || doc.status === "APPROVED" || doc.status === "REJECTED") && <StampBox doc={doc} />}
          </div>

          {/* 본문 */}
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13, marginBottom: 16 }}><tbody>
            <tr>
              <th style={{ border: "1px solid var(--border)", background: "var(--panel)", padding: "7px 10px", textAlign: "left", width: 130, fontWeight: 600, color: "var(--text-2)" }}>제목</th>
              <td style={{ border: "1px solid var(--border)", padding: "7px 10px", fontWeight: 600 }}>{doc.title}</td>
            </tr>
            {bodyKeys.map((k) => (
              <tr key={k}>
                <th style={{ border: "1px solid var(--border)", background: "var(--panel)", padding: "7px 10px", textAlign: "left", width: 130, fontWeight: 600, color: "var(--text-2)", verticalAlign: "top" }}>{fieldLabel(k)}</th>
                <td style={{ border: "1px solid var(--border)", padding: "7px 10px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{String(doc.body_json[k] ?? "")}</td>
              </tr>
            ))}
          </tbody></table>

          {/* 결재 의견 */}
          {doc.lines.some((l) => l.decision_comment) && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-3)", marginBottom: 6 }}>결재 의견</div>
              {doc.lines.filter((l) => l.decision_comment).map((l) => (
                <div key={l.id} style={{ fontSize: 12.5, marginBottom: 4 }}>
                  <b>{l.approver_name}</b> <Badge m={DECISION[l.decision] || DECISION.PENDING} /> {l.decision_comment} <span style={{ color: "var(--text-3)", fontSize: 11 }}>{fmtDate(l.decided_at)}</span>
                </div>
              ))}
            </div>
          )}

          {doc.references.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-3)", marginBottom: 8 }}>참조 · 수신</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 18 }}>
                {doc.references.map((r, i) => (
                  <span key={i} style={{ fontSize: 12, padding: "3px 9px", borderRadius: 12, background: "var(--panel)", color: "var(--text-2)" }}>
                    {r.name || `#${r.user_id}`}{r.read_at ? " ✓" : ""}
                  </span>
                ))}
              </div>
            </>
          )}

          {(doc.attachments?.length ?? 0) > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-3)", marginBottom: 8 }}>첨부 파일</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {doc.attachments!.map((a) => (
                  <button key={a.id} className="t-btn" style={{ justifyContent: "flex-start", fontSize: 12.5 }} onClick={() => downloadAtt(a)}>
                    📎 {a.file_name} <span style={{ color: "var(--text-3)", marginLeft: 6 }}>{(a.file_size / 1024).toFixed(0)} KB</span>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* 댓글 */}
          <div style={{ marginTop: 22, borderTop: "1px solid var(--border)", paddingTop: 14 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 10 }}>💬 댓글 {comments ? `(${comments.length})` : ""}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 12 }}>
              {comments?.length === 0 && <div style={{ fontSize: 12.5, color: "var(--text-3)" }}>첫 댓글을 남겨보세요</div>}
              {comments?.map((c) => (
                <div key={c.id} style={{ display: "flex", gap: 8 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 14, background: "var(--panel)", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "var(--text-2)" }}>{(c.author_name || "?").slice(0, 1)}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600 }}>{c.author_name || `#${c.author_id}`}</span>
                      <span style={{ fontSize: 11, color: "var(--text-3)" }}>{fmtDate(c.created_at)}</span>
                      {c.author_id === me && <button onClick={() => delComment(c.id)} style={{ marginLeft: "auto", border: "none", background: "none", cursor: "pointer", color: "var(--text-3)", fontSize: 11 }}>삭제</button>}
                    </div>
                    {c.body && <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word", marginTop: 2 }}>{c.body}</div>}
                    {c.att_name && (
                      <button onClick={() => downloadCommentAtt(c.id, c.att_name!)} className="t-btn" style={{ fontSize: 11.5, marginTop: 4, justifyContent: "flex-start" }}>
                        📎 {c.att_name}{c.att_size ? <span style={{ color: "var(--text-3)", marginLeft: 6 }}>{(c.att_size / 1024).toFixed(0)} KB</span> : null}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {cfile && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-2)", marginBottom: 6 }}>
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>📎 {cfile.name}</span>
                <button className="t-btn" style={{ fontSize: 11, padding: "1px 6px", color: "#ef4444" }} onClick={() => setCfile(null)}>✕</button>
              </div>
            )}
            <div style={{ display: "flex", gap: 6 }}>
              <label className="t-btn" style={{ fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center" }} title="파일 첨부">
                📎<input type="file" hidden onChange={(e) => { setCfile(e.target.files?.[0] ?? null); e.currentTarget.value = ""; }} />
              </label>
              <input className="t-input" style={{ flex: 1, fontSize: 13 }} placeholder="댓글 입력…" value={cbody}
                onChange={(e) => setCbody(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addComment(); }} />
              <button className="t-btn t-btn-primary" style={{ fontSize: 12 }} onClick={addComment} disabled={!cbody.trim() && !cfile}>등록</button>
            </div>
          </div>
        </div>

        {(canApprove || canRecall || canSubmit || canCancel) && (
          <div style={{ borderTop: "1px solid var(--border)", padding: "12px 20px", display: "flex", flexDirection: "column", gap: 8 }}>
            {canApprove && (
              <input className="t-input" style={{ fontSize: 13 }} placeholder="의견 (반려 시 필수)" value={comment} onChange={(e) => setComment(e.target.value)} />
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              {canSubmit && <button className="t-btn t-btn-primary" disabled={busy} onClick={() => act("submit")}>상신</button>}
              {canRecall && <button className="t-btn" disabled={busy} onClick={() => act("recall")}>↩ 회수</button>}
              {canCancel && <button className="t-btn" disabled={busy} style={{ color: "#ef4444" }} onClick={() => { if (confirm("이 문서를 취소하시겠습니까?")) act("cancel"); }}>취소</button>}
              {canApprove && <>
                <button className="t-btn" disabled={busy} style={{ color: "#ef4444" }} onClick={() => { if (!comment.trim()) { toast.error("반려 사유를 입력하세요"); return; } act("reject", { comment }); }}>반려</button>
                <button className="t-btn" disabled={busy} title="이후 결재를 생략하고 최종 승인" onClick={() => { if (confirm("전결 처리하시겠습니까? 이후 결재가 생략되고 최종 승인됩니다.")) act("approve", { comment: comment || null, final: true }); }}>전결</button>
                <button className="t-btn t-btn-primary" disabled={busy} onClick={() => act("approve", { comment: comment || null })}>승인</button>
              </>}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ── Inbox list ───────────────────────────────────────────────────────────────
function Inbox({ tab, me }: { tab: string; me: number | null }) {
  const [items, setItems] = useState<Doc[] | null>(null);
  const [selId, setSelId] = useState<number | null>(null);

  const load = useCallback(() => {
    setItems(null);
    api.get<Doc[]>(`/approval/inbox?tab=${tab}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [tab]);
  useEffect(() => { load(); setSelId(null); }, [load]);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div className="t-page-header" style={{ padding: "20px 24px 10px" }}>
        <div>
          <h1>{TAB_LABEL[tab] || "결재함"}</h1>
          <div className="subtitle">{items ? `${items.length}건` : ""}</div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "0 24px 24px" }}>
        {items === null && <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>불러오는 중…</div>}
        {items?.length === 0 && <div style={{ padding: 48, textAlign: "center", color: "var(--text-3)" }}>문서가 없습니다</div>}
        {items?.map((d) => (
          <div key={d.id} className="t-card" onClick={() => setSelId(d.id)}
            style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", marginBottom: 8, cursor: "pointer" }}>
            <Badge m={STATUS[d.status] || STATUS.DRAFT} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.title}</div>
              <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>{d.form_name || d.form_code} · {d.doc_no} · 기안 {d.author_name || `#${d.author_id}`} · {fmtDate(d.created_at)}</div>
            </div>
            {d.lines.length > 0 && <span style={{ fontSize: 11, color: "var(--text-3)", flexShrink: 0 }}>{d.current_step}/{Math.max(...d.lines.map((l) => l.step_order))} 단계</span>}
          </div>
        ))}
      </div>

      {selId != null && <Detail docId={selId} me={me} onClose={() => setSelId(null)}
        onChanged={() => { setSelId(null); load(); window.dispatchEvent(new Event("approval:refresh")); }} />}
    </div>
  );
}

// ── Delegation (위임 / 대결 설정) ─────────────────────────────────────────────
type Deleg = { id: number; delegate_id: number; delegate_name?: string | null; reason?: string | null; starts_at?: string | null; ends_at?: string | null; is_active: boolean };
function Delegation({ onClose }: { onClose: () => void }) {
  const me = useAuth((s) => s.user?.id ?? null);
  const [rows, setRows] = useState<Deleg[] | null>(null);
  const [delegate, setDelegate] = useState<PickUser[]>([]);
  const [reason, setReason] = useState("");
  const [ends, setEnds] = useState("");
  const load = useCallback(() => { api.get<Deleg[]>("/approval/delegations").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!delegate[0]) { toast.error("위임 받을 사람을 선택하세요"); return; }
    try {
      await api.post("/approval/delegations", { delegate_id: delegate[0].id, reason: reason || null, ends_at: ends ? new Date(ends).toISOString() : null });
      toast.success("위임이 등록되었습니다");
      setDelegate([]); setReason(""); setEnds(""); load(); window.dispatchEvent(new Event("approval:refresh"));
    } catch (e: any) { toast.error("등록 실패", e?.response?.data?.detail ?? ""); }
  };
  const del = async (id: number) => { try { await api.delete(`/approval/delegations/${id}`); load(); } catch { toast.error("삭제 실패"); } };

  return (
    <div style={{ height: "100%", overflowY: "auto" }}>
      <div style={{ maxWidth: 680, margin: "0 auto", padding: "24px 20px 48px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <button className="t-btn" onClick={onClose} style={{ fontSize: 12 }}>← 결재함</button>
          <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800 }}>위임 · 부재중 대결 설정</h2>
        </div>
        <p style={{ margin: "0 0 16px", fontSize: 13, color: "var(--text-3)" }}>내 결재를 다른 사람이 대신 처리(대결)할 수 있도록 위임합니다. 위임받은 사람의 결재함 '결재 대기'에 내 문서가 함께 보입니다.</p>

        <div className="t-card" style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
          <UserPicker label="위임 받을 사람" value={delegate} onChange={(v) => setDelegate(v.slice(-1))} excludeId={me} />
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <label className="t-label">사유 (선택)</label>
              <input className="t-input" style={{ width: "100%", fontSize: 13 }} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="예: 휴가 부재" />
            </div>
            <div>
              <label className="t-label">종료일 (선택)</label>
              <input type="date" className="t-input" style={{ fontSize: 13 }} value={ends} onChange={(e) => setEnds(e.target.value)} />
            </div>
          </div>
          <div style={{ textAlign: "right" }}><button className="t-btn t-btn-primary" onClick={create}>위임 등록</button></div>
        </div>

        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-3)", marginBottom: 8 }}>등록된 위임</div>
          {rows === null && <div style={{ fontSize: 13, color: "var(--text-3)" }}>불러오는 중…</div>}
          {rows?.length === 0 && <div style={{ fontSize: 13, color: "var(--text-3)" }}>등록된 위임이 없습니다</div>}
          {rows?.map((d) => (
            <div key={d.id} className="t-card" style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>→ {d.delegate_name || `#${d.delegate_id}`}</div>
                <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>{d.reason || "사유 없음"}{d.ends_at ? ` · ${fmtDate(d.ends_at)}까지` : ""}</div>
              </div>
              <button className="t-btn" style={{ fontSize: 12, color: "#ef4444" }} onClick={() => del(d.id)}>해제</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Dashboard (기본 페이지) ───────────────────────────────────────────────────
function ApprovalDashboard({ me, onNavTab, onCompose }: { me: number | null; onNavTab: (t: string) => void; onCompose: () => void }) {
  const [counts, setCounts] = useState<Record<string, number> | null>(null);
  const [waiting, setWaiting] = useState<Doc[]>([]);
  const [mine, setMine] = useState<Doc[]>([]);
  const [selId, setSelId] = useState<number | null>(null);

  const reload = useCallback(() => {
    api.get<Record<string, number>>("/approval/inbox/counts").then((r) => setCounts(r.data)).catch(() => {});
    api.get<Doc[]>("/approval/inbox?tab=waiting").then((r) => setWaiting(r.data.slice(0, 6))).catch(() => {});
    api.get<Doc[]>("/approval/inbox?tab=in_progress").then((r) => setMine(r.data.slice(0, 6))).catch(() => {});
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const cards: { k: string; label: string; icon: string; accent: string }[] = [
    { k: "waiting", label: "결재 대기", icon: "🖊️", accent: "#ef4444" },
    { k: "in_progress", label: "진행 중", icon: "⏳", accent: "#f59e0b" },
    { k: "drafts", label: "임시 저장", icon: "📝", accent: "#6b7280" },
    { k: "completed", label: "완료", icon: "✅", accent: "#16a34a" },
    { k: "rejected", label: "반려", icon: "⛔", accent: "#dc2626" },
    { k: "referenced", label: "수신·참조", icon: "👁️", accent: "#6366f1" },
  ];
  const list = (title: string, docs: Doc[], tab: string) => (
    <div className="t-card" style={{ padding: 0, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", padding: "12px 16px", borderBottom: "1px solid var(--border)" }}>
        <span style={{ fontWeight: 700, fontSize: 14, flex: 1 }}>{title}</span>
        <button className="t-btn t-btn-sm" onClick={() => onNavTab(tab)}>전체</button>
      </div>
      {docs.length === 0 && <div style={{ padding: 24, textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>문서 없음</div>}
      {docs.map((d) => (
        <div key={d.id} onClick={() => setSelId(d.id)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderTop: "1px solid var(--border)", cursor: "pointer" }}>
          <Badge m={STATUS[d.status] || STATUS.DRAFT} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.title}</div>
            <div style={{ fontSize: 11, color: "var(--text-3)" }}>{d.form_name || d.form_code} · 기안 {d.author_name || `#${d.author_id}`} · {fmtDate(d.created_at)}</div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ height: "100%", overflowY: "auto" }}>
      <div style={{ maxWidth: 960, margin: "0 auto", padding: "24px 24px 48px" }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 18 }}>
          <div style={{ flex: 1 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>전자결재</h1>
            <p style={{ margin: "2px 0 0", fontSize: 13, color: "var(--text-3)" }}>결재 현황 한눈에 보기</p>
          </div>
          <button className="t-btn t-btn-primary" onClick={onCompose}><I.Plus size={14} /> 새 결재 작성</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 22 }}>
          {cards.map((c) => (
            <div key={c.k} className="t-card" style={{ padding: "16px 18px", cursor: "pointer" }} onClick={() => onNavTab(c.k)}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 22 }}>{c.icon}</span>
                <div>
                  <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>{c.label}</div>
                  <div className="num" style={{ fontSize: 26, fontWeight: 800, color: (counts?.[c.k] ?? 0) > 0 ? c.accent : "var(--text-2)" }}>{counts ? counts[c.k] ?? 0 : "–"}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          {list("결재 대기", waiting, "waiting")}
          {list("내가 올린 진행 문서", mine, "in_progress")}
        </div>
      </div>

      {selId != null && <Detail docId={selId} me={me} onClose={() => setSelId(null)} onChanged={() => { setSelId(null); reload(); window.dispatchEvent(new Event("approval:refresh")); }} />}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function ApprovalPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("filter") || "waiting";
  const compose = params.get("compose");
  const me = useAuth((s) => s.user?.id ?? null);

  if (params.get("delegation")) {
    return <Delegation onClose={() => setParams({ filter: "waiting" })} />;
  }
  if (!params.get("filter") && !compose) {
    return <ApprovalDashboard me={me} onNavTab={(t) => setParams({ filter: t })} onCompose={() => setParams({ compose: "1" })} />;
  }
  if (compose) {
    return <Compose composeParam={compose}
      onDone={(to) => setParams({ filter: to })}
      onCancel={() => setParams({ filter: tab === "compose" ? "waiting" : tab })} />;
  }
  return <Inbox tab={tab} me={me} />;
}
